import { z } from 'zod';
import { isoDate } from './common.js';

/**
 * Схемы API страницы «Правила расчёта» (004): `specs/004-rules-editor/contracts/http-api.md`, data-model.md В, Г.
 * Маршруты `/api/rules/…` проверяют вход этими схемами; интерфейс читает ответы по ним. Интерфейс не зависит от
 * движка: тексты правил (вопрос, шаблон ответа, подписи полей) приходят в ответе сервера.
 */

const id = z.number().int().positive();
const text = (min: number, max: number) => z.string().trim().min(min).max(max);

// ── Блоки и поля ──

/** Блок набора правил (как в 002 и в файле правил): кубик, включён ли, режим, параметры, реализация. */
export const RuleBlock = z.object({
  cube: z.string().min(1),
  enabled: z.boolean(),
  mode: z.string().nullable().optional(),
  params: z.record(z.string(), z.unknown()),
  impl: z.number().int().positive().nullable(),
  basis: z.unknown().optional(),
});
export type RuleBlock = z.infer<typeof RuleBlock>;

/**
 * Поле правила для заказчика — форма движка с простыми подписями (`toPlainForm`): что рисовать в предложении ответа.
 * Лишние поля формы движка допускаются (`looseObject`): интерфейс читает только эти.
 */
export const RuleField = z.looseObject({
  name: z.string(),
  cube: z.string(),
  label: z.string(),
  hint: z.string().optional(),
  control: z.enum(['select', 'multiselect', 'integer', 'number', 'text', 'boolean', 'map', 'list', 'json']),
  required: z.boolean(),
  options: z
    .array(
      z.object({
        value: z.unknown(),
        doc: z.string().optional(),
        /** Расчёт этот вариант пока не учитывает — выбрать нельзя (16.10.2026). */
        unused: z.boolean().optional(),
      }),
    )
    .optional(),
  /** Расчёт поле пока не учитывает — изменить нельзя, рядом пометка (решение пользователя 16.10.2026). */
  unused: z.boolean().optional(),
  /** Поле меняет команда разработки, а не заказчик: в «Изменить» — значение строкой (16.10.2026). */
  teamOnly: z.boolean().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  unit: z.string().optional(),
  timeKind: z.string().optional(),
  locked: z.object({ value: z.unknown(), article: z.string() }).optional(),
  showWhen: z.object({ field: z.string(), in: z.array(z.unknown()) }).optional(),
});
export type RuleField = z.infer<typeof RuleField>;

// ── Тексты проверок, риски, изменения ──

/** Ошибка проверки набора у поля: код и простой текст (`plain-ru.checks`). */
export const CheckText = z.object({
  code: z.string(),
  cube: z.string().optional(),
  field: z.string().optional(),
  text: z.string(),
});
export type CheckText = z.infer<typeof CheckText>;

/** Предупреждение о рискованном сочетании (research R7): текст, пример, связанные правила, отпечаток, отметка. */
export const RiskText = z.object({
  code: z.string(),
  text: z.string(),
  example: z.string().optional(),
  related: z.array(z.string()).default([]),
  fields: z.array(z.string()).default([]),
  fingerprint: z.string(),
  acked: z.boolean(),
});
export type RiskText = z.infer<typeof RiskText>;

/** Изменение словами («Обед не короче: 30 мин → 40 мин»), data-model А.5. */
export const Change = z.object({
  group: z.string(),
  field: z.string(),
  label: z.string(),
  before: z.unknown(),
  after: z.unknown(),
  beforeText: z.string(),
  afterText: z.string(),
  text: z.string(),
});
export type Change = z.infer<typeof Change>;

// ── Основание и версии ──

/** «Почему меняем» (FR-034): четыре варианта публикации. */
export const PUBLISH_BASIS_KINDS = [
  'решение_руководства',
  'приказ_распоряжение',
  'исправление_ошибки',
  'изменение_закона',
] as const;
/** Все виды основания версии: перенос из файла, публикация, возврат (data-model В.1). */
export const BASIS_VIEW_KINDS = ['перенос', 'ответ_на_вопрос', ...PUBLISH_BASIS_KINDS, 'возврат'] as const;

export const QUESTION_CODE_RE = /^Q-\d{2,3}$/;

/** Основание версии для показа: «Почему так», история, плашка. */
export const BasisView = z.object({
  kind: z.string(),
  decided: z.string(),
  doc: z.string().optional(),
  date: z.string().optional(),
  questionCodes: z.array(z.string()).default([]),
  rollbackOf: z.number().int().optional(),
});
export type BasisView = z.infer<typeof BasisView>;

/** Основание публикации из окна «Опубликовать»: номер и дата документа обязательны у приказа. */
export const PublishBasis = z
  .object({
    kind: z.enum(PUBLISH_BASIS_KINDS),
    decided: text(1, 1000),
    doc: text(1, 100).optional(),
    questionCode: z.string().regex(QUESTION_CODE_RE).optional(),
  })
  .refine((b) => b.kind !== 'приказ_распоряжение' || b.doc !== undefined, {
    message: 'Укажите номер и дату документа',
    path: ['doc'],
  });
export type PublishBasis = z.infer<typeof PublishBasis>;

/** Метка версии на экране (data-model Г.3): действует, действовала, начнёт действовать, заменена возвратом, не введена. */
export const VERSION_LABELS = ['active', 'past', 'future', 'replaced', 'not_introduced'] as const;
export const VersionLabel = z.enum(VERSION_LABELS);
export type VersionLabel = z.infer<typeof VersionLabel>;

/** Итог пересчёта версии (FR-041, FR-040). */
export const RecalcResult = z.object({
  status: z.enum(['queued', 'running', 'done', 'failed']),
  days: z.number().int().nonnegative().optional(),
  changed: z.number().int().nonnegative().optional(),
  failedDays: z.number().int().nonnegative().optional(),
  /** Дни, которые пересчёт не посчитал (FR-041): человек и дата. */
  failed: z.array(z.object({ personId: z.string(), name: z.string(), date: isoDate })).optional(),
  mismatch: z.number().int().nonnegative().optional(),
});
export type RecalcResult = z.infer<typeof RecalcResult>;

export const VersionView = z.object({
  id,
  version: z.number().int().positive(),
  validFrom: isoDate,
  label: VersionLabel,
  basis: BasisView,
  publishedAt: z.string(),
  author: z.object({ name: z.string() }).nullable(),
  changes: z.array(Change),
  recalc: RecalcResult.optional(),
  notIntroduced: z.object({ reason: z.string(), at: z.string() }).optional(),
  /**
   * «Вернуть эти правила» у версии «действовала» (US4, сценарий 6): `available: false` и причина — кнопка недоступна
   * сразу, до окна. `impl_gone` — в сборке нет реализации кубика версии (`IMPL_GONE`).
   */
  rollback: z.object({ available: z.boolean(), reason: z.enum(['impl_gone']).optional() }).optional(),
  /** Версия опубликована по плану ИИ-агента (005, FR-039: «Подготовлено ИИ-агентом»). */
  preparedByAgent: z.boolean().optional(),
});
export type VersionView = z.infer<typeof VersionView>;

// ── Разделы, правила, термины, черновик: ответ `GET /api/rules` ──

export const RuleSectionView = z.object({
  id: z.string(),
  n: z.number().int().min(1).max(8),
  title: z.string(),
  sentence: z.string(),
});
export type RuleSectionView = z.infer<typeof RuleSectionView>;

export const RuleGroupView = z.object({
  id: z.string(),
  section: z.string(),
  order: z.number().int(),
  question: z.string(),
  /** Шаблон ответа ICU MessageFormat и карта аргументов «имя → кубик.поле» (data-model А.2). */
  answer: z.string(),
  args: z.record(z.string(), z.string()),
  /** Поля под ответом (`кубик.поле`): строка «подпись: значение», в изменении — поле ввода. */
  details: z.array(z.string()).default([]),
  fields: z.array(RuleField),
  /** Значения полей по ключу `кубик.поле`. */
  values: z.record(z.string(), z.unknown()),
  /** Текст значения поля по ключу `кубик.поле`: число с единицей, текст варианта, список (`valueText`). */
  texts: z.record(z.string(), z.string()).default({}),
  law: z.object({ article: z.string() }).optional(),
  fixed: z.literal('system').optional(),
  offWhen: z.object({ field: z.string(), value: z.unknown() }).optional(),
  needs: z.array(z.string()),
  unmetNeeds: z.array(z.string()),
  registry: z.array(z.string()),
  dependsOn: z.array(z.string()),
  usedBy: z.array(z.string()),
  why: BasisView.nullable(),
  /** Правило закона: постоянная часть строки «Почему так» (`ANSWERS[id].why`). */
  lawWhy: z.string().optional(),
  inCatalog: z.boolean(),
  /** Пример дня у правила есть (`RuleGroup.noExample` — нет): у общих правил блока примера нет (T106). */
  hasExample: z.boolean().default(true),
  /**
   * Сколько вариантов полей скрыто: им нужен график работы, а на объекте графиков нет (текущее значение не скрывается;
   * решение пользователя 16.10.2026). «Изменить» пишет, что они появятся вместе с графиками.
   */
  hiddenOptions: z.number().int().nonnegative().default(0),
  /** Что меняет правило, если не часы дней (`AnswerTemplate.effect`): для строки влияния. */
  effect: z.string().optional(),
  draft: z
    .object({
      values: z.record(z.string(), z.unknown()),
      /** Тексты значений черновика по `кубик.поле` (`valueText`), как `texts` правила. */
      texts: z.record(z.string(), z.string()).default({}),
      changed: z.boolean(),
      errors: z.array(CheckText),
      risks: z.array(RiskText),
    })
    .optional(),
});
export type RuleGroupView = z.infer<typeof RuleGroupView>;

/** Термин страницы; `editor` — слово редактора правил (видят те, кто правит или утверждает правила). */
export const TermView = z.object({ term: z.string(), text: z.string(), editor: z.boolean().default(false) });
export type TermView = z.infer<typeof TermView>;

export const DraftView = z.object({
  id,
  etag: z.string(),
  baseVersionId: id.nullable(),
  baseLabel: VersionLabel.nullable(),
  baseValidFrom: isoDate.nullable(),
  leader: z.object({ userId: id, name: z.string() }).nullable(),
  isLeader: z.boolean(),
  changes: z.array(Change),
  errors: z.array(CheckText),
  risks: z.array(RiskText),
  /** FR-035: начало самого раннего неподписанного табеля, не раньше границы; граница — последняя опубликованная. */
  defaultValidFrom: isoDate,
  earliestValidFrom: isoDate,
  /** Кому ведущий может передать черновик: другие администраторы правил объекта. Только у ведущего (FR-028). */
  transferTo: z.array(z.object({ userId: id, name: z.string() })).optional(),
});
export type DraftView = z.infer<typeof DraftView>;

export const RulesView = z.object({
  date: isoDate,
  today: isoDate,
  version: z.object({
    id,
    version: z.number().int().positive(),
    validFrom: isoDate,
    label: VersionLabel,
    basis: BasisView,
    publishedAt: z.string(),
    author: z.object({ name: z.string() }).nullable(),
  }),
  periodVersions: z
    .array(z.object({ id, version: z.number().int().positive(), validFrom: isoDate }))
    .optional(),
  sections: z.array(RuleSectionView),
  groups: z.array(RuleGroupView),
  terms: z.array(TermView),
  draft: DraftView.nullable().optional(),
  /**
   * 007, поправка 20.10.2026 (FR-004a): правила к запуску площадки, которая ещё не запущена. `version` — версия образца,
   * от которой построены правила; `sourceChanged` — образец опубликовал новую версию после начала черновика.
   */
  launch: z
    .object({
      source: z.string(),
      sourceName: z.string(),
      sourceVersion: z.number().int().positive(),
      sourceChanged: z.boolean(),
    })
    .optional(),
  newVersionsSinceVisit: z
    .array(
      z.object({ id, version: z.number().int().positive(), validFrom: isoDate, publishedAt: z.string() }),
    )
    .optional(),
});
export type RulesView = z.infer<typeof RulesView>;

export const RulesViewQuery = z.object({
  date: isoDate.optional(),
  /** Период табеля при открытии из «Как считаются часы»: версии периода — `periodVersions`. */
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type RulesViewQuery = z.infer<typeof RulesViewQuery>;

// ── День: пример, «Выбрать день», расчёт дня ──

/** Состояние дня — как `day_results.state` (`ck_day_results_state`). */
export const DAY_VIEW_STATES = ['ok', 'нет_выхода', 'нет_входа', 'на_объекте', 'пусто'] as const;

export const DayView = z.object({
  state: z.enum(DAY_VIEW_STATES),
  provisional: z.boolean(),
  /** Проход: местное время объекта «чч:мм», в другую дату — «дд.мм чч:мм» (как `DayMark` карточки дня). */
  passes: z.array(z.object({ at: z.string(), dir: z.enum(['in', 'out']) })),
  cells: z.array(z.object({ row: z.string(), hours: z.number() })),
  total: z.number(),
  /** Первая строка объяснения расчёта дня — тот же текст, что в карточке дня (FR-015). */
  why: z.string(),
  questions: z.array(z.string()),
  error: z.object({ code: z.string() }).optional(),
  /**
   * Строки расчёта дня с правилом экрана (`DayReceiptRow.ruleGroup`): пример правила показывает свои строки — у обеда
   * «Обед: − 1 ч (на объекте больше 4 ч)» (T106, план коррекции по ASD-STE100).
   */
  steps: z
    .array(z.object({ group: z.string(), label: z.string(), note: z.string(), value: z.string() }))
    .default([]),
});
export type DayView = z.infer<typeof DayView>;

export const ExampleDay = z.union([
  z.object({
    personId: z.string(),
    name: z.string(),
    date: isoDate,
    day: DayView,
    golden: z.boolean().optional(),
  }),
  z.object({ none: z.literal(true), reason: z.literal('no_days') }),
]);
export type ExampleDay = z.infer<typeof ExampleDay>;

export const ExampleQuery = z.object({ group: z.string().min(1), date: isoDate.optional() });

/**
 * Правка одного правила экрана: значения полей правила по ключу `кубик.поле`. Блоки собирает сервер поверх базы —
 * `enabled`, `impl` и режим блока берутся из базы, связка — целиком, поля других правил того же кубика не меняются
 * (data-model Г.2).
 */
export const GroupEdit = z.object({
  group: z.string().min(1).max(64),
  values: z.record(z.string().max(128), z.unknown()),
});
export type GroupEdit = z.infer<typeof GroupEdit>;

/** Кандидат для расчёта: правка поверх последней опубликованной версии (`version`) или черновика (`draft`). */
export const Candidate = GroupEdit.extend({ base: z.enum(['version', 'draft']) });
export type Candidate = z.infer<typeof Candidate>;

export const DayRequest = z.object({
  personId: z.string().min(1),
  date: isoDate,
  candidate: Candidate.optional(),
});
export type DayRequest = z.infer<typeof DayRequest>;
export const DayResponse = z.object({ now: DayView, after: DayView.optional(), changed: z.boolean() });
export type DayResponse = z.infer<typeof DayResponse>;

export const PeopleQuery = z.object({ query: text(2, 50) });
export const PeopleResponse = z.array(z.object({ personId: z.string(), name: z.string() })).max(20);
export type PeopleResponse = z.infer<typeof PeopleResponse>;

export const DayExplainQuery = z.object({ personId: z.string().min(1), date: isoDate });

// ── Влияние ──

export const ImpactDaySide = z.object({
  state: z.string(),
  cells: z.record(z.string(), z.number()),
});

export const ImpactDay = z.object({
  personId: z.string(),
  name: z.string(),
  date: isoDate,
  before: ImpactDaySide,
  after: ImpactDaySide,
  questions: z.object({ plus: z.array(z.string()), minus: z.array(z.string()) }),
  edited: z.boolean(),
  /** День с правкой: часы правки (FR-029, «Дни с правкой»). */
  manualHours: z.number().nullable().optional(),
  error: z.object({ code: z.string() }).optional(),
});
export type ImpactDay = z.infer<typeof ImpactDay>;

export const ImpactSummary = z.object({
  changedDays: z.number().int().nonnegative(),
  people: z.number().int().nonnegative(),
  plusMs: z.number().int().nonnegative(),
  minusMs: z.number().int().nonnegative(),
  questionsPlus: z.number().int().nonnegative(),
  questionsMinus: z.number().int().nonnegative(),
  editedDays: z.number().int().nonnegative(),
  signedDays: z.number().int().nonnegative(),
  /** Дни, которые кандидат не может посчитать (ошибка расчёта): в «изменились» не входят (T106). */
  errorDays: z.number().int().nonnegative().default(0),
  /** Из изменившихся дней — те, где часы были, а после изменения не осталось ни одного (приёмка 16.10.2026). */
  zeroedDays: z.number().int().nonnegative().default(0),
});
export type ImpactSummary = z.infer<typeof ImpactSummary>;

export const GoldenResult = z.object({
  goldenId: id,
  code: z.string(),
  title: z.string().nullable(),
  passed: z.boolean(),
  before: z.unknown(),
  after: z.unknown(),
  /** У черновика отмечено «Так и должно быть» для этого примера (`draft_golden_stale`). */
  confirmed: z.boolean().optional(),
});
export type GoldenResult = z.infer<typeof GoldenResult>;

export const Impact = z.object({
  summary: ImpactSummary,
  days: z.array(ImpactDay),
  goldens: z.array(GoldenResult),
  computedAt: z.string(),
  durationMs: z.number().int().nonnegative(),
});
export type Impact = z.infer<typeof Impact>;

export const ImpactRequest = Candidate.extend({
  draftEtag: z.string().optional(),
  validFrom: isoDate.optional(),
  /** Номер запроса интерфейса: ответ на старый номер интерфейс не показывает. */
  token: z.string().min(1).max(64),
});
export type ImpactRequest = z.infer<typeof ImpactRequest>;

export const ImpactResponse = z.object({
  thisChange: Impact,
  wholeDraft: Impact.optional(),
  /** Ошибки проверки набора с правкой — у поля (FR-024, `plain-ru.checks`). */
  errors: z.array(CheckText).default([]),
  /** Предупреждения набора с правкой (FR-025). */
  risks: z.array(RiskText).default([]),
  mark: z.object({ maxCalcSeq: z.number().int(), maxActionId: z.number().int() }),
});
export type ImpactResponse = z.infer<typeof ImpactResponse>;

// ── Черновик ──

export const SaveBlocksRequest = GroupEdit.extend({ etag: z.string().optional() });
export type SaveBlocksRequest = z.infer<typeof SaveBlocksRequest>;
/** Правка равна базе — изменение убрано; черновик без изменений удалён или не создан: `draft` и `etag` — `null`. */
export const SaveBlocksResponse = z.object({
  draft: DraftView.nullable(),
  etag: z.string().nullable(),
  errors: z.array(CheckText),
  risks: z.array(RiskText),
});
export type SaveBlocksResponse = z.infer<typeof SaveBlocksResponse>;

export const RevertRequest = z.object({ etag: z.string(), group: z.string().min(1) });
export const TransferRequest = z.object({ etag: z.string(), toUserId: id });
export const TakeRequest = z.object({ etag: z.string() });
export const DiscardQuery = z.object({ etag: z.string() });
export const DiscardBody = z.object({ reason: text(1, 500).optional() });
export const DraftResponse = z.object({ draft: DraftView.nullable(), etag: z.string().nullable() });
export type DraftResponse = z.infer<typeof DraftResponse>;

export const PreviewRequest = z.object({ etag: z.string(), validFrom: isoDate });
export const PreviewResponse = z.object({
  previewId: id,
  status: z.enum(['computing', 'fresh', 'stale', 'failed']),
  summary: ImpactSummary.optional(),
  days: z.array(ImpactDay).optional(),
  goldens: z.array(GoldenResult).optional(),
  risks: z.array(RiskText),
  errors: z.array(CheckText),
  /** Неподписанные табели, которые дата начала делит на две версии правил (FR-035, сценарий 2). */
  splitTimesheets: z
    .array(z.object({ id, name: z.string(), periodStart: isoDate, periodEnd: isoDate }))
    .optional(),
});
export type PreviewResponse = z.infer<typeof PreviewResponse>;

export const EtagBody = z.object({ etag: z.string() });
export const RiskAckRequest = z.object({ etag: z.string(), fingerprint: z.string().min(1) });

export const PublishRequest = z.object({
  etag: z.string(),
  previewId: id,
  validFrom: isoDate,
  basis: PublishBasis,
  seen: z.literal(true),
  /** Публикация по плану ИИ-агента (005, FR-038): сервер сверяет отпечаток плана с черновиком. */
  planId: id.optional(),
});
export type PublishRequest = z.infer<typeof PublishRequest>;
export const PublishResponse = z.object({ version: VersionView, jobId: id.nullable() });
export type PublishResponse = z.infer<typeof PublishResponse>;

// ── Версии: возврат и «Не вводить» ──

export const ReasonRequest = z.object({ reason: text(1, 500) });
export type ReasonRequest = z.infer<typeof ReasonRequest>;
export const RollbackResponse = z.object({ version: VersionView, jobId: id });
export type RollbackResponse = z.infer<typeof RollbackResponse>;
export const NotIntroduceResponse = z.object({ version: VersionView });
export type NotIntroduceResponse = z.infer<typeof NotIntroduceResponse>;

export const HistoryQuery = z.object({ from: isoDate.optional(), to: isoDate.optional() });
export const HistoryResponse = z.array(VersionView);

// ── Проверочные примеры ──

export const PinGoldenRequest = z.object({ personId: z.string().min(1), date: isoDate, title: text(3, 120) });
export type PinGoldenRequest = z.infer<typeof PinGoldenRequest>;

export const GoldenView = z.object({
  id,
  code: z.string(),
  title: z.string().nullable(),
  date: isoDate,
  source: z.enum(['checkDay', 'dayExplain', 'demo0', 'repo']),
  createdBy: z.object({ name: z.string() }).nullable(),
  createdAt: z.string(),
  expected: z.unknown(),
  draft: z
    .object({ passed: z.boolean(), before: z.unknown(), after: z.unknown(), confirmed: z.boolean() })
    .optional(),
});
export type GoldenView = z.infer<typeof GoldenView>;
/** `POST /api/rules/goldens`. */
export const PinGoldenResponse = z.object({ golden: GoldenView });
export type PinGoldenResponse = z.infer<typeof PinGoldenResponse>;
/** `GET /api/rules/goldens`: действующие примеры; при черновике — с результатом по черновику. */
export const GoldensResponse = z.array(GoldenView);
export type GoldensResponse = z.infer<typeof GoldensResponse>;

// ── Просьбы о правилах и вопросы реестра ──

export const RULE_REQUEST_STATUSES = ['waiting', 'in_registry', 'rejected'] as const;

export const RuleRequestView = z.object({
  id,
  text: z.string(),
  author: z.object({ name: z.string() }),
  createdAt: z.string(),
  status: z.enum(RULE_REQUEST_STATUSES),
  questionCode: z.string().nullable(),
  rejectReason: z.string().nullable(),
});
export type RuleRequestView = z.infer<typeof RuleRequestView>;

export const CreateRuleRequest = z.object({ text: text(1, 2000) });
export const RuleRequestCheckResponse = z.object({ warnings: z.array(z.literal('personal_data')) });
export type RuleRequestCheckResponse = z.infer<typeof RuleRequestCheckResponse>;
export const CreateRuleRequestResponse = z.object({
  request: RuleRequestView,
  warnings: z.array(z.literal('personal_data')),
});
export type CreateRuleRequestResponse = z.infer<typeof CreateRuleRequestResponse>;
/** `GET /api/rules/requests`: просьбы объекта, новые сверху. */
export const RuleRequestsResponse = z.array(RuleRequestView);
export type RuleRequestsResponse = z.infer<typeof RuleRequestsResponse>;
/** `POST /api/rules/requests/:id/status`. */
export const SetRuleRequestStatusResponse = z.object({ request: RuleRequestView });
export type SetRuleRequestStatusResponse = z.infer<typeof SetRuleRequestStatusResponse>;
export const SetRuleRequestStatus = z.discriminatedUnion('status', [
  z.object({ status: z.literal('in_registry'), questionCode: z.string().regex(QUESTION_CODE_RE) }),
  z.object({ status: z.literal('rejected'), reason: text(1, 500) }),
]);
export type SetRuleRequestStatus = z.infer<typeof SetRuleRequestStatus>;

export const RegistryQuestionView = z.object({
  code: z.string().regex(QUESTION_CODE_RE),
  title: z.string(),
  source: z.enum(['file', 'request']),
});
export const RegistryResponse = z.array(RegistryQuestionView);
export type RegistryResponse = z.infer<typeof RegistryResponse>;

// ── Команда: изменения после выгрузки ──

export const TeamChange = z.object({
  versionId: id,
  event: z.enum(['published', 'rolled_back', 'not_introduced', 'mismatch']),
  at: z.string(),
  author: z.object({ name: z.string() }).nullable(),
  validFrom: isoDate,
  questionCode: z.string().nullable(),
  marks: z.array(z.string()),
  impact: ImpactSummary.nullable(),
});
export type TeamChange = z.infer<typeof TeamChange>;
export const TeamChangesResponse = z.array(TeamChange);
export const ExportQuery = z.object({ version: z.coerce.number().int().positive().optional() });

// ── Пересчёт после публикации и возврата: плашка табеля, история дня (004, FR-042–FR-044, data-model Г.5, Г.6) ──

/** Сторона изменения дня в пересчёте — как `ruleset_recalc_changes.before/after`. */
export const RecalcDaySide = z.object({
  state: z.string(),
  /** Часы по строкам Т-13 (показанные R5). */
  cells: z.record(z.string(), z.number()),
  /** Вопросы бригадиру — коды пометок. */
  questions: z.array(z.string()),
});
export type RecalcDaySide = z.infer<typeof RecalcDaySide>;

/** Плашка пересчёта в табеле: что изменилось в днях табеля после публикации или возврата версии. */
export const RecalcNotice = z.object({
  jobId: id,
  versionId: id,
  validFrom: isoDate,
  publishedAt: z.string(),
  basis: BasisView,
  /** Изменения правил словами (`diffVersions`). */
  changes: z.array(Change),
  days: z.array(
    z.object({
      personId: z.string(),
      date: isoDate,
      before: RecalcDaySide,
      after: RecalcDaySide,
      /** В дне правка, и пересчёт изменил машинные часы под ней (R13). */
      staleEdit: z.boolean(),
    }),
  ),
  people: z.number().int().nonnegative(),
  /** Ещё задач пересчёта с изменениями в этом табеле, кроме показанной («Ещё N пересчётов»). */
  more: z.number().int().nonnegative(),
  /** С какой даты эти задачи («Ещё N пересчётов с {дата}»); `more = 0` — поля нет. */
  moreSince: isoDate.optional(),
});
export type RecalcNotice = z.infer<typeof RecalcNotice>;

/** Запись пересчёта в истории дня (карточка дня, `GET …/calc`). */
export const RecalcHistoryEntry = z.object({
  kind: z.literal('recalc'),
  /** Время пересчёта (ISO). */
  at: z.string(),
  versionId: id,
  validFrom: isoDate,
  basis: BasisView,
  before: RecalcDaySide,
  after: RecalcDaySide,
});
export type RecalcHistoryEntry = z.infer<typeof RecalcHistoryEntry>;

/** Заметка у подписанного дня (`signed_notes`): табель подписан, а правила потом изменились. */
export const SIGNED_NOTE_CODES = [
  'после_подписи_опубликована_версия',
  'подписан_по_отменённой_версии',
  'E10',
  'расхождение_со_снимком',
] as const;
export const SignedNote = z.object({
  code: z.enum(SIGNED_NOTE_CODES),
  at: z.string(),
  versionId: id.optional(),
  data: z.record(z.string(), z.unknown()).optional(),
});
export type SignedNote = z.infer<typeof SignedNote>;
